import React, { useState, useEffect, useRef } from 'react'
import { globalState } from '../../../../store'
import styled from 'styled-components'
import { useParams } from 'react-router-dom'
import { usePost, useFetch } from '../../../../components/component-items/helpers'
import { OrderInfo } from './styles'

// Components
import GlobalStyles from '../../../../components/component-items/styles'
import Checkbox from '../../../../components/component-items/checkbox'
import { Form, InputGroup } from 'react-bootstrap'
import CardLoading from '../../../../components/component-items/loading-popover'
import { HoverNotes } from '../../../../components/component-items/hover-notes'
import { PermissionGate } from '../../../../components/rbac'
import { usePermissions } from '../../../../hooks/usePermissions'

const Label = styled(Form.Label)`
  margin-top: 1em;
`

const SectionHeader = styled.h5`
  margin-top: 1.5em;
  margin-bottom: 0.5em;
  padding-bottom: 0.5em;
  border-bottom: 1px solid #e0e0e0;
  color: #666;
  font-size: 0.9em;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`

export const ProductDetails = () => {
  const productVariant = useParams<{ id: string }>().id
  const url = `/api/product/variant/${productVariant}/details/`
  const {
    state: { csrf },
  } = globalState()

  const { response: resp, loaded, error }: any = useFetch(url, {})

  const productDetails = loaded ? resp.productDetails : {}
  const { hasAccess } = usePermissions()

  const {
    response: countryResp,
    loaded: countryLoaded,
    error: countryError,
  }: any = useFetch('/api/address/countries/', {})
  const countries: any = countryLoaded ? countryResp.countries : []

  const defaultVariant = {
    title: '',
    description: '',
    hsCode: '',
    inventoryControl: '',
    inventoryControlCategory: 'received_date',
    syncInventory: false,
    signatureRequired: false,
    adultSignatureRequired: false,
    countryOfOrigin: 'US',
    nmfc_code: '',
    nmfc_description: '',
    freight_class: '',
    promote: false,
  }

  const [variant, setVariant] = useState(defaultVariant)
  const initialPromote = useRef(false)
  const isKasePackaging = loaded ? resp.isKasePackaging : false
  const canManagePromotion = hasAccess('settings', 'admin')

  const handleUpdate = () => {
    const payload: Partial<typeof variant> = { ...variant }
    if (!isKasePackaging || !canManagePromotion || variant.promote === initialPromote.current) {
      delete payload.promote
    }
    usePost(url, payload, csrf)
  }

  useEffect(() => {
    if (loaded) {
      if (Object.keys(productDetails).length !== 0 && productDetails.constructor === Object) {
        setVariant(productDetails)
        initialPromote.current = Boolean(productDetails.promote)
      } else {
        setVariant(defaultVariant)
        initialPromote.current = false
      }
    }
  }, [loaded])

  return (
    <OrderInfo style={{ display: 'block', position: 'relative', height: 'unset' }}>
      <GlobalStyles.CardHeader style={{ padding: '0 12px' }}>
        <GlobalStyles.CardTitle>
          <h3>Variant Details</h3>
        </GlobalStyles.CardTitle>
      </GlobalStyles.CardHeader>
      <div style={{ minHeight: 500, width: '100%', overflow: 'hidden' }}>
        <Form.Group style={{ margin: '1em' }}>
          <SectionHeader style={{ marginTop: 0 }}>General</SectionHeader>
          <Label>Title</Label>
          <Form.Control type="text" value={variant.title || ''} disabled />
          <Label>Description</Label>
          <Form.Control as="textarea" value={variant.description || ''} disabled />
          <SectionHeader>Shipping & Customs</SectionHeader>
          <Label>HS Code</Label>
          <Form.Control
            type="text"
            value={variant.hsCode || ''}
            onChange={(e) => setVariant({ ...variant, hsCode: e.target.value })}
          />
          <Label>
            <HoverNotes
              description="Where the product is manufactured. This is used for shipping and customs purposes."
              title="Country of Origin"
            />
          </Label>
          <Form.Select
            value={variant.countryOfOrigin || ''}
            onChange={(e) => setVariant({ ...variant, countryOfOrigin: e.target.value })}
          >
            {Object.keys(countries).map((c, id) => (
              <option value={c} key={id}>
                {countries[c]}
              </option>
            ))}
          </Form.Select>
          <Form.Control.Feedback type="invalid">Please provide a valid country.</Form.Control.Feedback>
          <Label>
            <HoverNotes
              description="National Motor Freight Classification code for LTL shipping (e.g., 123456-01)"
              title="NMFC Code"
            />
          </Label>
          <Form.Control
            type="text"
            placeholder="e.g., 123456-01"
            value={variant.nmfc_code || ''}
            onChange={(e) => setVariant({ ...variant, nmfc_code: e.target.value })}
          />
          <Label>NMFC Description</Label>
          <Form.Control
            type="text"
            placeholder="e.g., Machinery, NOI"
            value={variant.nmfc_description || ''}
            onChange={(e) => setVariant({ ...variant, nmfc_description: e.target.value })}
          />
          <Label>
            <HoverNotes
              description="Freight class determines shipping rates for LTL handling units (50-500)"
              title="Freight Class"
            />
          </Label>
          <Form.Select
            value={variant.freight_class || ''}
            onChange={(e) => setVariant({ ...variant, freight_class: e.target.value })}
          >
            <option value="">-- Select Freight Class --</option>
            <option value="50">Class 50</option>
            <option value="55">Class 55</option>
            <option value="60">Class 60</option>
            <option value="65">Class 65</option>
            <option value="70">Class 70</option>
            <option value="77.5">Class 77.5</option>
            <option value="85">Class 85</option>
            <option value="92.5">Class 92.5</option>
            <option value="100">Class 100</option>
            <option value="110">Class 110</option>
            <option value="125">Class 125</option>
            <option value="150">Class 150</option>
            <option value="175">Class 175</option>
            <option value="200">Class 200</option>
            <option value="250">Class 250</option>
            <option value="300">Class 300</option>
            <option value="400">Class 400</option>
            <option value="500">Class 500</option>
          </Form.Select>
          <InputGroup style={{ marginTop: '1em' }}>
            <Checkbox
              selected={variant.signatureRequired || false}
              setSelected={() => setVariant({ ...variant, signatureRequired: !variant.signatureRequired })}
            />
            <p style={{ padding: '0 1em', lineHeight: '24px' }}>Signature Required</p>
          </InputGroup>
          <InputGroup>
            <Checkbox
              selected={variant.adultSignatureRequired || false}
              setSelected={() => setVariant({ ...variant, adultSignatureRequired: !variant.adultSignatureRequired })}
            />
            <p style={{ padding: '0 1em', lineHeight: '24px' }}>Adult Signature Required</p>
          </InputGroup>
          <SectionHeader>Inventory & Fulfillment</SectionHeader>
          <Label>Inventory Control</Label>
          <Form.Control type="text" value={variant.inventoryControl || ''} disabled />
          <Label>Inventory Control Category</Label>
          <Form.Control type="text" value={variant.inventoryControlCategory || ''} disabled />
          <InputGroup style={{ marginTop: '1em' }}>
            <Checkbox
              selected={variant.syncInventory || false}
              setSelected={() => setVariant({ ...variant, syncInventory: !variant.syncInventory })}
            />
            <p style={{ padding: '0 1em' }}>
              <HoverNotes
                description="This does NOT initiate a sync. This lets Kase know that when syncing occurs to include this variant."
                title="Sync Inventory"
              />
            </p>
          </InputGroup>
          {isKasePackaging ? (
            <PermissionGate segment="settings" minLevel="admin">
              <InputGroup>
                <Checkbox
                  selected={variant.promote || false}
                  setSelected={() => setVariant({ ...variant, promote: !variant.promote })}
                />
                <p style={{ padding: '0 1em' }}>
                  <HoverNotes
                    description="Include this package variant in the pricing tool's promoted Kase packaging options."
                    title="Promote in Pricing Tool"
                  />
                </p>
              </InputGroup>
            </PermissionGate>
          ) : null}
        </Form.Group>
        <div style={{ float: 'right' }}>
          <GlobalStyles.Button onClick={handleUpdate}>Update</GlobalStyles.Button>
        </div>
      </div>
      {!loaded ? <CardLoading error={error} /> : null}
    </OrderInfo>
  )
}
